import { cn } from "@/lib/utils";
import elevateClient from "@/services/elevate/elevate-client";
import makeSlug from "@/utils/makeSlug";
import Flag from "@app/_components/Flag";
import { createProductRules } from "@server/requests/elevate/createProductRules";
import { Heading } from "@ui/heading";
import { Paragraph } from "@ui/paragraph";
import { deleteCookie, setCookie } from "cookies-next";
import { useRouter } from "next/navigation";
import { ComponentProps, MouseEvent } from "react";
import {
  LANGUAGE_COOKIE_NAME,
  MARKET_COOKIE_NAME,
  SUGGESTION_COOKIE_NAME,
} from "../config/cookies";
import { Market } from "../config/i18n-config";

export default function MarketSelectorItem({
  market,
  className,
}: { market: Market } & ComponentProps<"li">) {
  const router = useRouter();

  async function onClick(e: MouseEvent<HTMLAnchorElement>) {
    e.preventDefault();
    setCookie(MARKET_COOKIE_NAME, market.countryCode);
    setCookie(LANGUAGE_COOKIE_NAME, market.languageCode);
    deleteCookie(SUGGESTION_COOKIE_NAME);

    let pathname = "";

    const session = window.sessionStorage.getItem("siblings");

    if (!session) {
      pathname = makeSlug(market.siteLocale);
    } else if (session.includes("product")) {
      const [, productKey] = session.split(":");
      const [variantKey] = productKey.split("__");
      const [{ productRules }] = createProductRules({
        attribute: "variant_key",
        values: [variantKey],
      });
      const response = await elevateClient(market).query.landingPage(
        { pageReference: "/intercept-route" },
        {
          primaryList: {
            include: true,
            productRules,
          },
        },
      );
      const product = response.primaryList?.productGroups?.[0]?.products?.[0];
      const variant = product?.variants[0];
      if (variant?.link) {
        pathname += makeSlug(market.siteLocale, variant.link);
      }
    } else {
      try {
        const links = JSON.parse(session ?? "{}");
        if (links[market.languageCode]) {
          pathname += makeSlug(market.siteLocale, links[market.languageCode]);
        }
      } catch {}
    }

    router.push(market.domain + pathname);
  }

  return (
    <li className={cn("block", className)}>
      <a
        data-market={market.countryCode}
        data-lang={market.languageCode}
        onClick={onClick}
        href={market.domain + makeSlug(market.siteLocale)}
        className="flex items-center gap-2 bg-gray-50 p-3 whitespace-nowrap transition-colors focus-within:bg-gray-100 hover:bg-gray-100"
      >
        <Flag
          code={market.countryCode}
          className="h-4 w-4 rounded-full border"
        />
        <Heading as="h3" className="grow">
          {market.countryName}
        </Heading>
        <Paragraph className="text-gray-400 uppercase">
          {market.languageCode} / {market.currency}
        </Paragraph>
      </a>
    </li>
  );
}
